<template lang="pug">
.curso-main-container.pb-3
  BannerInterno
  .container.tarjeta.tarjeta--blanca.p-4.p-md-5.mb-5
    .titulo-principal.color-acento-contenido
      .titulo-principal__numero
        span 4
      h1 Aspectos para tener en cuenta sobre el estrés laboral

    p El estrés laboral es una respuesta frecuente ante las exigencias del entorno de trabajo y puede afectar tanto el bienestar personal como el desempeño profesional. Su impacto depende no solo de las condiciones laborales, sino también de la forma en que cada persona percibe y gestiona dichas exigencias. Por ello, es importante considerar aspectos como el reconocimiento de los límites personales, la percepción de los factores estresantes, la gestión del tiempo, la comunicación asertiva y una adecuada organización y delegación del trabajo, especialmente desde los roles directivos, con el fin de prevenir efectos negativos y promover entornos laborales saludables.

    SlyderF(columnas="col-lg-4 col-md-6" data-aos="fade-down")
      .tarjeta.bg-img2.p-4
        .row.justify-content-center.mb-3
          .col-6.col-md-8.col-lg-5
            img(src='@/assets/curso/temas/t4/tarj1-1.svg', alt='')
        h4.text-center Reconocimiento de los límites personales
        p Reconocer que todas las personas tienen un límite físico, emocional y mental es fundamental. Identificar oportunamente ese límite y no sobrepasarlo contribuye directamente a la preservación de la salud, el bienestar y la calidad de vida, tanto en el ámbito personal como laboral (Melgosa, 2006, p. 22).
      .tarjeta.bg-img2.p-4
        .row.justify-content-center.mb-3
          .col-6.col-md-8.col-lg-5
            img(src='@/assets/curso/temas/t4/tarj1-2.svg', alt='')
        h4.text-center Formas de percibir los factores estresantes
        p Los factores estresantes no afectan de igual manera a todas las personas; estos pueden ser evaluados de tres formas: como una amenaza, como un daño irreparable o como un reto. La forma en que se percibe la situación influye directamente en la respuesta emocional y conductual frente al estrés.
      .tarjeta.bg-img2.p-4
        .row.justify-content-center.mb-3
          .col-6.col-md-8.col-lg-5
            img(src='@/assets/curso/temas/t4/tarj1-3.svg', alt='')
        h4.text-center El estrés no siempre es negativo
        p El estrés, cuando se presenta en niveles moderados, puede convertirse en un factor motivador que impulse el rendimiento, la creatividad y la productividad. El problema surge cuando se vuelve intenso o prolongado.
      .tarjeta.bg-img2.p-4
        .row.justify-content-center.mb-3
          .col-6.col-md-8.col-lg-5
            img(src='@/assets/curso/temas/t4/tarj1-4.svg', alt='')
        h4.text-center Gestión del tiempo
        p Una adecuada planificación del tiempo permite reducir la presión innecesaria. Evitar dejar las actividades para última hora ayuda a disminuir la ansiedad y favorece un mejor desempeño.
      .tarjeta.bg-img2.p-4
        .row.justify-content-center.mb-3
          .col-6.col-md-8.col-lg-5
            img(src='@/assets/curso/temas/t4/tarj1-5.svg', alt='')
        h4.text-center Comunicación asertiva
        p Es importante mantener una comunicación clara y respetuosa con los superiores, especialmente en lo relacionado con la asignación y delegación de tareas, con el fin de evitar sobrecargas laborales.
      .tarjeta.bg-img2.p-4
        .row.justify-content-center.mb-3
          .col-6.col-md-8.col-lg-5
            img(src='@/assets/curso/temas/t4/tarj1-6.svg', alt='')
        h4.text-center Responsabilidad directiva
        p En el caso de quienes ocupan cargos directivos, es fundamental organizar el trabajo de manera equitativa, delegar funciones de forma adecuada y evitar concentrar excesivas cargas laborales en una sola persona.


    Separador
    #t_4_1.titulo-segundo.color-acento-contenido(data-aos='fade-right')
      h2 4.1 Síndrome de desgaste personal o #[em burnout]

    p El término #[em burnout] se atribuye al doctor H. Freudenberger y se define como un “síndrome de agotamiento físico y mental intenso, resultado de un estrés laboral crónico o de una frustración prolongada” (Rojo y Cervera, 2005, p. 43). 

    .row.justify-content-center.align-items-center
      .col-lg-4.col-md-9.mb-4.mb-lg-0(data-aos="fade-right")
        .titulo-sexto.color-acento-contenido.mb-3
          h5 Figura 1.
          span Síndrome de #[em burnout]
        figure
          img(src='@/assets/curso/temas/t4/fig1.png', alt='Representa a una persona agotada en su espacio de trabajo, con signos claros de cansancio físico y emocional. Alrededor se integran elementos que simbolizan estrés. Reflejando el impacto integral del síndrome de desgaste personal en la vida laboral y personal.')
      .col-lg-8(data-aos="fade-left")
        p Este síndrome no suele originarse por un ataque intencional, sino principalmente por deficiencias en la organización del trabajo, la persona afectada experimenta agotamiento profesional, sensación de sobrecarga y pérdida de energía; si estas condiciones persisten, pueden aparecer síntomas como irritabilidad, depresión e incluso el abandono del trabajo.
        .bg4.p-4
          .subt2.mb-4
            h4.mb-0.px-3 Principales factores asociados a su aparición
          ul.lista-ul--color.fa-lista
            li
              i.fas.fa-puzzle-piece
              span.mb-0 Insuficiencia de personal frente a múltiples funciones y sobrecarga laboral.
            li
              i.fas.fa-puzzle-piece
              span.mb-0 Retribución desproporcionada (económica, reconocimiento o incentivos) en relación con las responsabilidades asumidas.
            li
              i.fas.fa-puzzle-piece
              span.mb-0 Falta de autonomía para la toma de decisiones y ausencia de recursos para cumplir con las tareas asignadas.
            li
              i.fas.fa-puzzle-piece
              span.mb-0 Rutina excesiva y falta de estímulos.
            li
              i.fas.fa-puzzle-piece
              span.mb-0 Escaso apoyo o interés por parte de los directivos.


    Separador
    #t_4_2.titulo-segundo.color-acento-contenido(data-aos='fade-right')
      h2 4.2 Acoso laboral

    .row.justify-content-center.align-items-center.mb-5
      .col-lg-6(data-aos="fade-right")
        p El objetivo del acoso laboral es la destrucción emocional de la persona y, en muchos casos, su salida definitiva de la organización. Se trata de un fenómeno complejo y difícil de detectar, que exige especial cuidado para evitar represalias contra la víctima o la protección involuntaria del acosador, quien en ocasiones aparenta amabilidad o falsas intenciones de ayuda. 
        .cajon.color-secundario.bg3.p-4.mb-4
          p.mb-0 A lo largo de la vida, muchas personas han experimentado algún tipo de acoso, ya sea en el ámbito familiar, conyugal, social o laboral. El acoso psicológico en el trabajo, también denominado #[em mobbing] o #[em bullying], ha sido identificado como una de las formas más extendidas y dañinas, responsable del deterioro psíquico y físico de millones de trabajadores (Rojo y Cervera, 2005, p. 15).
      .col-lg-6(data-aos="fade-left")
        img(src='@/assets/curso/temas/t4/1.png', alt='')

    .subt.mb-4(data-aos="fade-right")
      img(src="@/assets/curso/temas/t4/subt.svg")
      h3.mb-0.p-2.ps-lg-4.pe-lg-5 ¿Cómo surge el acoso laboral?

    p El acoso puede originarse por múltiples causas, entre ellas:

    .row.justify-content-center.align-items-center.mb-5
      .col-lg-5.d-none.d-lg-block(data-aos="fade-right")
        img(src='@/assets/curso/temas/t4/2.png', alt='')
      .col-lg-7(data-aos="fade-left")
        ol.lista-ol--cuadro.lista-ol--separador.fa-lista
          li 
            .lista-ol--cuadro__vineta
              span.text-dark.fw-bold a
            span.mb-0 Envidia o celos profesionales frente al reconocimiento, desempeño o proyección de la víctima.
          li 
            .lista-ol--cuadro__vineta
              span.text-dark.fw-bold b
            span.mb-0 Represalias contra personas con alto rendimiento que evidencian el bajo compromiso de otros.
          li 
            .lista-ol--cuadro__vineta
              span.text-dark.fw-bold c
            span.mb-0 Discriminación por razones de credo, raza, género, ideología, gustos personales o condiciones físicas.
          li 
            .lista-ol--cuadro__vineta
              span.text-dark.fw-bold d
            span.mb-0 Percepción de la víctima como emocionalmente más vulnerable.
          li 
            .lista-ol--cuadro__vineta
              span.text-dark.fw-bold e
            span.mb-0 Falta de aceptación dentro de la organización, incluso con acoso promovido desde niveles jerárquicos superiores.
          li 
            .lista-ol--cuadro__vineta
              span.text-dark.fw-bold f
            span.mb-0 Abuso de poder, manipulación, desorden jerárquico y deficiencias en la gestión de conflictos y la comunicación institucional.

    .subt2.mb-4(data-aos="fade-right")
      h4.mb-0.px-3 Tipos de personas que suelen ejercer el acoso

    p Entre los perfiles más comunes se encuentran:

    SlyderF.mb-5(columnas="col-lg-3 col-md-6" data-aos="fade-down")
      .tarjeta.bg-img2.p-4
        .row.justify-content-center.mb-3
          .col-9.col-lg-11
            img(src='@/assets/curso/temas/t4/tarj2-1.svg', alt='')
        p.text-center La persona intrigante y manipuladora.
      .tarjeta.bg-img2.p-4
        .row.justify-content-center.mb-3
          .col-9.col-lg-11
            img(src='@/assets/curso/temas/t4/tarj2-2.svg', alt='')
        p.text-center El directivo intolerante al trabajo colaborativo.
      .tarjeta.bg-img2.p-4
        .row.justify-content-center.mb-3
          .col-9.col-lg-11
            img(src='@/assets/curso/temas/t4/tarj2-3.svg', alt='')
        p.text-center El envidioso obsesionado con el salario o reconocimiento ajeno.
      .tarjeta.bg-img2.p-4
        .row.justify-content-center.mb-3
          .col-9.col-lg-11
            img(src='@/assets/curso/temas/t4/tarj2-4.svg', alt='')
        p.text-center El tirano que gobierna mediante el miedo.
      .tarjeta.bg-img2.p-4
        .row.justify-content-center.mb-3
          .col-9.col-lg-11
            img(src='@/assets/curso/temas/t4/tarj2-5.svg', alt='')
        p.text-center La persona agresiva, irascible o frustrada.
      .tarjeta.bg-img2.p-4
        .row.justify-content-center.mb-3
          .col-9.col-lg-11
            img(src='@/assets/curso/temas/t4/tarj2-6.svg', alt='')
        p.text-center El crítico constante que descalifica a los demás.
      .tarjeta.bg-img2.p-4
        .row.justify-content-center.mb-3
          .col-9.col-lg-11
            img(src='@/assets/curso/temas/t4/tarj2-7.svg', alt='')
        p.text-center Los colaboradores pasivos que respaldan o facilitan el acoso. 

    .subt2.mb-4(data-aos="fade-right")
      h4.mb-0.px-3 Tipos de hostigamiento laboral

    .row.justify-content-center.align-items-center.mb-4
      .col-lg-7(data-aos="fade-right")
        p El acoso puede manifestarse mediante diversas conductas, entre ellas:
        .bg1.p-4.py-lg-5
          ol.lista-ol--cuadro.fa-lista.mb-0
            li 
              .lista-ol--cuadro__vineta
                span.text-dark.fw-bold a
              span.mb-0 Difamación y desprestigio profesional.
            li 
              .lista-ol--cuadro__vineta
                span.text-dark.fw-bold b
              span.mb-0 Ocultamiento o boicot  de información relevante.
            li 
              .lista-ol--cuadro__vineta
                span.text-dark.fw-bold c
              span.mb-0 Exclusión social y aislamiento.
            li 
              .lista-ol--cuadro__vineta
                span.text-dark.fw-bold d
              span.mb-0 Amenazas verbales o escritas.
            li 
              .lista-ol--cuadro__vineta
                span.text-dark.fw-bold e
              span.mb-0 Ridiculización y humillación pública.
            li 
              .lista-ol--cuadro__vineta
                span.text-dark.fw-bold f
              span.mb-0 Cambios injustificados de cargo o ubicación.
            li 
              .lista-ol--cuadro__vineta
                span.text-dark.fw-bold g
              span.mb-0 Sobrecarga intencional de trabajo y desconocimiento de horarios.
            li.mb-0
              .lista-ol--cuadro__vineta
                span.text-dark.fw-bold h
              span.mb-0 Invasión de la vida privada y uso indebido de información personal.
      .col-lg-5.d-none.d-lg-block(data-aos="fade-left")
        img(src='@/assets/curso/temas/t4/4.png', alt='')

    p.mb-5 Estas conductas pueden presentarse de forma simultánea o progresiva. Detectarlas a tiempo es clave para prevenir la consolidación del #[em mobbing].

    .subt2.mb-4(data-aos="fade-right")
      h4.mb-0.px-3 Conflicto laboral o colectivo   

    p La mayoría de los conflictos laborales se originan por el incumplimiento de las normas y garantías legales, entre las cuales se destacan:

    .row.justify-content-center.mb-5(data-aos="fade-down")
      .col-lg-3.col-md-6.mb-3
        .tarjeta-avatar-b.h-100
          .tarjeta-avatar-b__img
            img(src='@/assets/curso/temas/t4/tarj3-1.svg' alt='')
          .tarjeta.bg-color-acento-contenido.h-100
            .p-4
              p.mb-0 Incumplimiento en el pago de la seguridad social.
      .col-lg-3.col-md-6.mb-3
        .tarjeta-avatar-b.h-100
          .tarjeta-avatar-b__img
            img(src='@/assets/curso/temas/t4/tarj3-2.svg' alt='')
          .tarjeta.bg-color-acento-contenido.h-100
            .p-4
              p.mb-0 Despidos masivos o injustificados.       
      .col-lg-3.col-md-6.mb-3
        .tarjeta-avatar-b.h-100
          .tarjeta-avatar-b__img
            img(src='@/assets/curso/temas/t4/tarj3-3.svg' alt='')
          .tarjeta.bg-color-acento-contenido.h-100
            .p-4
              p.mb-0 Desigualdad entre escalafones y salarios. 
      .col-lg-3.col-md-6.mb-3
        .tarjeta-avatar-b.h-100
          .tarjeta-avatar-b__img
            img(src='@/assets/curso/temas/t4/tarj3-4.svg' alt='')
          .tarjeta.bg-color-acento-contenido.h-100
            .p-4
              p.mb-0 Falta de pago del salario mínimo legal.
      .col-lg-3.col-md-6.mb-3
        .tarjeta-avatar-b.h-100
          .tarjeta-avatar-b__img
            img(src='@/assets/curso/temas/t4/tarj3-5.svg' alt='')
          .tarjeta.bg-color-acento-contenido.h-100
            .p-4
              p.mb-0 Irrespeto de la jornada laboral. 
      .col-lg-3.col-md-6.mb-3
        .tarjeta-avatar-b.h-100
          .tarjeta-avatar-b__img
            img(src='@/assets/curso/temas/t4/tarj3-6.svg' alt='')
          .tarjeta.bg-color-acento-contenido.h-100
            .p-4
              p.mb-0 Falta de condiciones de seguridad y salud en el trabajo.       
      .col-lg-3.col-md-6.mb-3
        .tarjeta-avatar-b.h-100
          .tarjeta-avatar-b__img
            img(src='@/assets/curso/temas/t4/tarj3-7.svg' alt='')
          .tarjeta.bg-color-acento-contenido.h-100
            .p-4
              p.mb-0 Discriminación por género. 
      .col-lg-3.col-md-6.mb-3
        .tarjeta-avatar-b.h-100
          .tarjeta-avatar-b__img
            img(src='@/assets/curso/temas/t4/tarj3-8.svg' alt='')
          .tarjeta.bg-color-acento-contenido.h-100
            .p-4
              p.mb-0 Despido de mujeres en estado de embarazo. 

    .subt2.mb-4(data-aos="fade-right")
      h4.mb-0.px-3 Organismos que regulan los conflictos laborales
    
    .row.justify-content-center(data-aos="fade-down")
      .col-lg-3.col-md-6.col-9.mb-3.mb-lg-0
        .tarjeta-avatar
          img(src='@/assets/curso/temas/t4/tarj4-1.svg', alt='')
          .tarjeta.bg2.w-100.text-center
            .p-4
              p.mb-0 Oficinas de Recursos Humanos.
      .col-lg-3.col-md-6.col-9.mb-3.mb-lg-0
        .tarjeta-avatar
          img(src='@/assets/curso/temas/t4/tarj4-2.svg', alt='')
          .tarjeta.bg2.w-100.text-center
            .p-4
              p.mb-0 Sindicatos y agremiaciones.
      .col-lg-3.col-md-6.col-9.mb-3.mb-lg-0
        .tarjeta-avatar
          img(src='@/assets/curso/temas/t4/tarj4-3.svg', alt='')
          .tarjeta.bg2.w-100.text-center
            .p-4
              p.mb-0 En Colombia, el #[b Ministerio del Trabajo].
      .col-lg-3.col-md-6.col-9.mb-3.mb-lg-0
        .tarjeta-avatar
          img(src='@/assets/curso/temas/t4/tarj4-4.svg', alt='')
          .tarjeta.bg2.w-100.text-center
            .p-4
              p.mb-0 A nivel internacional, la #[b Organización Internacional del Trabajo (OIT)].



</template>

<script>
export default {
  name: 'Tema4',
  data: () => ({
    // variables de vue
  }),
  mounted() {
    this.$nextTick(() => {
      this.$aosRefresh()
    })
  },
  updated() {
    this.$aosRefresh()
  },
}
</script>

<style lang="sass"></style>
